import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, Radio, Select, Stack, Text } from '@mantine/core';
import { useMutation, useQuery } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { userService } from '@/api/services/UserService';
import { getApiMessage } from '@/api/errors';
import { useAuth } from '@/stores/authContext';
import { formatCompanyLabel, formatOrgName } from '@/lib/format';
import type { CompanyCard, ConsigneeDetails } from '@/types/buy';
import { priceListFieldId } from './priceListForm';

const MIN_QUERY = 3;
const DEBOUNCE_MS = 350;

export type ConsigneeType = 'buyer' | 'other';

interface ConsigneeFieldsProps {
  /** Грузополучатель по умолчанию: сервер определяет его по базису (терминал — его владелец, иначе покупатель). */
  readonly defaultConsignee?: ConsigneeDetails;
  readonly type: ConsigneeType;
  readonly consignee: CompanyCard | null;
  readonly onType: (type: ConsigneeType) => void;
  readonly onConsignee: (company: CompanyCard | null) => void;
  /** Была попытка отправки: незаполненное отмечается только после неё. */
  readonly attempted: boolean;
}

/** Реквизиты организации одной строкой: «ИНН … · КПП … · ОГРН … · ОКПО …». */
function requisitesLine(
  t: (key: string) => string,
  values: { readonly inn?: string; readonly kpp?: string; readonly ogrn?: string; readonly okpo?: string },
): string {
  return (
    [
      ['auth.fields.inn', values.inn],
      ['app.company.card.kpp', values.kpp],
      ['app.company.card.ogrn', values.ogrn],
      ['app.company.card.okpo', values.okpo],
    ] as const
  )
    .filter(([, value]) => value)
    .map(([key, value]) => `${t(key)} ${value}`)
    .join(' · ');
}

// Грузополучатель прайс-листа: по умолчанию или другая организация из поиска. Поиск не отправляет на сервер подпись
// уже выбранной организации (сервер отвечает на такую строку ошибкой 500), сбой поиска показывается отдельно от
// «ничего не найдено».
export default function ConsigneeFields({ defaultConsignee, type, consignee, onType, onConsignee, attempted }: ConsigneeFieldsProps) {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  // Подпись, которую Mantine подставляет в поле при выборе: по ней не ищем, пока пользователь её не изменит.
  const [pickedLabel, setPickedLabel] = useState<string | null>(null);

  const selectedLabel = consignee?.inn ? formatCompanyLabel(consignee.name, consignee.inn, t('auth.fields.inn')) : null;
  const term = input.trim();
  const isLabel = term === pickedLabel || term === selectedLabel;

  useEffect(() => {
    const handle = window.setTimeout(() => setQuery(isLabel ? '' : term), DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [term, isLabel]);

  const search = useQuery({
    queryKey: ['companySearch', query] as const,
    queryFn: ({ signal }) => userService.searchCompanies(query, signal),
    enabled: isAuthenticated && type === 'other' && query.length >= MIN_QUERY,
    staleTime: 60_000,
  });

  const pick = useMutation({
    mutationFn: (inn: string) => userService.getCompanyByInn(inn),
    onSuccess: (company) => {
      if (company) {
        onConsignee(company);
        return;
      }
      setPickedLabel(null);
      notifications.show({ color: 'red', message: t('app.buy.form.consigneeNotFound') });
    },
    onError: (error) => {
      setPickedLabel(null);
      notifications.show({ color: 'red', message: getApiMessage(error) ?? t('auth.errors.generic') });
    },
  });

  const options = useMemo(() => {
    const list = (search.data ?? [])
      .filter((item) => item.inn)
      .map((item) => ({ value: item.inn!, label: formatCompanyLabel(item.name, item.inn!, t('auth.fields.inn')) }));
    if (consignee?.inn && selectedLabel && !list.some((option) => option.value === consignee.inn)) {
      list.unshift({ value: consignee.inn, label: selectedLabel });
    }
    return list;
  }, [search.data, consignee, selectedLabel, t]);

  const waiting = !isLabel && term.length >= MIN_QUERY && term !== query;
  const searching = type === 'other' && (waiting || search.isFetching);
  const nothingFound =
    term.length < MIN_QUERY || isLabel
      ? t('app.buy.form.consigneeTypeMore')
      : searching
        ? t('common.searching')
        : search.isError
          ? t('app.buy.form.consigneeSearchError')
          : t('app.buy.nothingFound');

  const defaultName = formatOrgName(defaultConsignee?.consigneeName) ?? t('app.buy.form.consigneeBuyer');
  const defaultRequisites = requisitesLine(t, {
    inn: defaultConsignee?.consigneeInn,
    kpp: defaultConsignee?.consigneeKpp,
  });

  return (
    <Stack gap="md">
      <Radio.Group
        id={priceListFieldId('consignee')}
        value={type}
        onChange={(value) => onType(value === 'other' ? 'other' : 'buyer')}
        aria-label={t('app.buy.form.consignee')}
      >
        <Stack gap="sm">
          <Radio
            value="buyer"
            label={
              <span>
                <Text component="span" fw={500}>
                  {t('app.buy.form.consigneeDefaultOption', { name: defaultName })}
                </Text>
                {defaultRequisites ? (
                  <Text component="span" display="block" size="sm" c="gray.7">
                    {defaultRequisites}
                  </Text>
                ) : null}
              </span>
            }
          />
          <Radio value="other" label={<Text component="span" fw={500}>{t('app.buy.form.consigneeOther')}</Text>} />
        </Stack>
      </Radio.Group>
      {type === 'other' ? (
        <Stack gap="sm" pl={{ base: 0, sm: 32 }}>
          <Select
            id={priceListFieldId('consigneeSearch')}
            label={t('app.buy.form.consigneeSearchLabel')}
            placeholder={t('app.buy.form.consigneeSearch')}
            searchable
            clearable
            data={options}
            filter={({ options: all }) => all}
            searchValue={input}
            onSearchChange={setInput}
            value={consignee?.inn ?? null}
            onChange={(inn, option) => {
              if (inn) {
                setPickedLabel(option.label);
                pick.mutate(inn);
              } else {
                setPickedLabel(null);
                onConsignee(null);
              }
            }}
            nothingFoundMessage={nothingFound}
            rightSection={searching || pick.isPending ? <Loader size={16} /> : undefined}
            disabled={pick.isPending}
            error={attempted && !consignee?.inn ? t('auth.validation.required') : undefined}
            maw={560}
          />
          {consignee ? (
            <div>
              <Text fw={600}>{formatOrgName(consignee.name) ?? '—'}</Text>
              <Text size="sm">{requisitesLine(t, consignee)}</Text>
              {consignee.address ? (
                <Text size="sm" c="gray.7">
                  {consignee.address}
                </Text>
              ) : null}
            </div>
          ) : null}
        </Stack>
      ) : null}
    </Stack>
  );
}
